import type { Locale } from "./locales.js";
import type { Corners } from "./georeference.js";
import type { Inks } from "./theme.js";
import type { PoiCategory } from "./poi.js";

/**
 * Paquet hors ligne : un festival entier en un fichier.
 *
 * C'est la pièce qui rend l'application utilisable réseau coupé. Elle le
 * télécharge une fois, puis ne parle plus à l'API pendant l'événement.
 *
 * Toutes les langues livrées y figurent, et non la seule langue courante : les
 * données d'un festival pèsent quelques dizaines de kilo-octets, alors qu'un
 * changement de langue sur place, sans réseau, serait autrement impossible.
 */

/** Incrémenté à chaque changement incompatible du format. */
export const BUNDLE_FORMAT_VERSION = 1;

/** Un libellé dans les langues disponibles ; la langue de rédaction est garantie. */
export type Translated = Partial<Record<Locale, string>>;

export type BundleFestival = {
  id: string;
  slug: string;
  status: "COMMUNITY" | "OFFICIAL";
  countryCode: string;
  timezone: string;
  festivalDayStartHour: number;
  defaultLocale: Locale;
  startsAt: string;
  endsAt: string;
  name: Translated;
  description: Translated;
  /**
   * Encres du thème de l'organisateur, si celui-ci en a fourni un.
   *
   * Absentes pour un festival communautaire : sa page emprunterait sinon
   * l'identité de quelqu'un qui n'a rien demandé.
   */
  themeInks?: Inks;
  /**
   * Plan du site, calé sur la carte.
   *
   * Une image posée sur quatre coins, et non une archive de tuiles : la
   * transformation affine donne les coins exacts, rotation comprise, et un plan
   * de festival pèse quelques mégaoctets — le découper serait un travail pour
   * rien.
   */
  /**
   * Orthophoto de drone.
   *
   * Le paquet annonce son poids, et rien de plus : le téléchargement n'a pas
   * lieu tout seul. Imposer deux cents mégaoctets à un téléphone sur le réseau
   * d'un festival serait hostile, et l'application fonctionne très bien sans.
   */
  orthophoto?: {
    url: string;
    bytes: number;
    minZoom: number;
    maxZoom: number;
  };
  /**
   * Home-screen icon, official festivals only: an adaptive icon the app offers
   * to pin as a shortcut. `version` changes with the image, so a phone knows
   * when to download it again.
   */
  launcherIcon?: { url: string; version: string };
  /**
   * Where a community page's lineup comes from, and the day it was last
   * checked there (CONTENU.md §1). Community festivals only: an organiser's
   * own data needs no source.
   */
  lineupSource?: { label: string | null; url: string | null; checkedOn: string | null };
  sitePlan?: {
    /** Adresse de l'image, relative à l'API. */
    url: string;
    /**
     * Changes with the image, so a phone that has the plan knows a new one is
     * out. Absent from bundles made before 2026-10-13.
     */
    version?: string;
    /** Haut-gauche, haut-droit, bas-droit, bas-gauche. */
    corners: Corners;
    /** Incertitude du calage, affichée à qui veut savoir ce que vaut le plan. */
    accuracyM: number;
  };
};

export type BundleZone = {
  id: string;
  category: string;
  name: Translated;
  geojson: unknown;
};

export type BundlePoi = {
  id: string;
  category: PoiCategory;
  latitude: number;
  longitude: number;
  verified: boolean;
  name: Translated;
  description: Translated;
};

export type BundlePath = {
  id: string;
  wheelchairAccessible: boolean;
  geojson: unknown;
};

export type BundleStage = {
  id: string;
  position: number;
  name: Translated;
};

export type BundleArtist = {
  id: string;
  name: string;
  genres: string[];
};

export type BundlePerformance = {
  id: string;
  stageId: string;
  artistId: string;
  startsAt: string;
  endsAt: string;
  /** Journée festivalière au format `YYYY-MM-DD`, déjà résolue côté serveur. */
  festivalDay: string;
  status: "SCHEDULED" | "DELAYED" | "CANCELLED" | "REPLACED";
};

export type OfflineBundle = {
  formatVersion: number;
  generatedAt: string;
  /**
   * Empreinte du contenu, pour ne retélécharger que si quelque chose a changé.
   */
  contentHash: string;
  festival: BundleFestival;
  zones: BundleZone[];
  pois: BundlePoi[];
  paths: BundlePath[];
  stages: BundleStage[];
  artists: BundleArtist[];
  performances: BundlePerformance[];
};

/** Résout un libellé traduit, avec repli sur la langue de rédaction du festival. */
export function localized(value: Translated, locale: Locale, fallback: Locale): string {
  return value[locale] ?? value[fallback] ?? Object.values(value)[0] ?? "";
}
